import _ from 'lodash';
import { mix } from 'polished';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Heading3, Paragraph, tokens } from '@treams/treams-ui';

const Leader = styled(Box)``;

const GroupName = styled(Paragraph)`
	color: ${tokens.text.secondary};
	height: 36px;
	line-height: 36px;
	margin-bottom: 4px;
`;

const Main = styled(Box)``;

const Top = styled(Flex)``;

const CategoryName = styled(Box)`
	color: ${tokens.text.secondary};
	writing-mode: vertical-lr;
	transform: scale(-1, -1);
	width: 36px;
	line-height: 36px;
	margin-right: 4px;
`;

const Body = styled(Box)``;

const Row = styled(Flex)`
	margin-bottom: 4px;
`;

const Delta = styled(Flex)`
	position: absolute;
	width: 100%;
	height: 100%;
	align-items: center;
	justify-content: center;
	top: 0;
	left: 0;
	background: white;
	transition: 0.2s ease-out opacity;
	opacity: 0;
`;

const Arrow = styled(Box)<{ up: boolean }>`
	position: absolute;

	margin-top: ${(props) => (props.up ? -20 : 20)}px;

	svg {
		display: block;
		width: 24px;
		height: 24px;

		transform: rotate(${(props) => (props.up ? -90 : 90)}deg);

		path {
			fill: ${(props) => (props.up ? props.theme.colors.green : props.theme.colors.red)};
		}
	}
`;

const Cell = styled(Box)<{ value?: number }>`
	position: relative;
	background: ${(props) =>
		typeof props.value === 'number'
			? props.value >= 0.5
				? mix(props.value * 2 - 1, props.theme.colors.green, props.theme.colors.white)
				: mix(props.value * 2, props.theme.colors.white, props.theme.colors.red)
			: tokens.surface.secondary};
	width: 36px;
	height: 36px;
	flex-grow: 0;
	flex-shrink: 0;
	margin-right: 4px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: bold;
	line-height: 36px;
	text-align: center;

	&:hover ${Delta} {
		opacity: 1;
	}
`;

function Score(props: { value?: number; delta?: number; scale?: number; suffix?: string }) {
	const scale = props.scale ?? 1;

	return (
		<Cell
			value={
				props.value ? (props.value * (typeof props.scale === 'number' ? 10 : 1) - 1) / 9 : undefined
			}
		>
			{typeof props.value === 'number'
				? `${(props.value * scale).toFixed(props.suffix ? 0 : 1)}${props.suffix ?? ''}`
				: '-'}
			{typeof props.delta === 'number' && (
				<Delta>
					{Math.abs(props.delta * scale).toFixed(1)}
					<Arrow up={props.delta >= 0}>
						<ChevronRightIcon />
					</Arrow>
				</Delta>
			)}
		</Cell>
	);
}

type AnalyticsEngagementGroupParticipation =
	| graphql.AnalyticsEngagementGroupParticipation
	| { group?: graphql.Group; participation?: number; delta?: number };

export default function AllScoresTable(props: {
	scores: graphql.AnalyticsEngagementGroupSubcategoryScore[];
	participation: AnalyticsEngagementGroupParticipation[];
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const groups = _.sortBy(
		_.uniqBy(
			props.scores.map((score) => score.group),
			'id',
		),
		[Boolean, (group: graphql.Group) => group?.name ?? ''],
	) as graphql.Group[];

	const categories = _.sortBy(
		_.uniqBy(
			props.scores.map((score) => score.subcategory),
			'id',
		),
		[
			Boolean,
			(category: graphql.EngagementSubcategory) => (category ? localize(category.title).text : ''),
		],
	) as graphql.EngagementSubcategory[];

	const scores = new Map<string, graphql.AnalyticsEngagementGroupSubcategoryScore>();

	for (const score of props.scores) {
		scores.set([score.group?.id ?? '', score.subcategory?.id ?? ''].join('-'), score);
	}

	const participation = new Map<string, AnalyticsEngagementGroupParticipation>();

	for (const record of props.participation) {
		participation.set(record.group?.id ?? '', record);
	}

	return (
		<Flex alignItems="flex-end" width="max-content">
			<Leader mr={2}>
				<Heading3 mb={2}>{t('dashboard.metrics.engagement.allScores.title')}</Heading3>
				{groups.map((group) => {
					if (group?.isArchived) return null;

					return (
						<GroupName key={group?.id ?? ''}>
							{group?.name ?? t('dashboard.metrics.engagement.allScores.organization')}
						</GroupName>
					);
				})}
			</Leader>
			<Main>
				<Top mb={2}>
					{categories.map((category) => (
						<CategoryName key={category?.id ?? ''}>
							{category
								? localize(category?.title).text
								: t('dashboard.metrics.engagement.allScores.overall')}
						</CategoryName>
					))}
					<CategoryName ml={4}>
						{t('dashboard.metrics.engagement.allScores.participation')}
					</CategoryName>
				</Top>
				<Body>
					{groups.map((group) => {
						if (group?.isArchived) return null;

						const rate = participation.get(group?.id ?? '');

						return (
							<Row key={group?.id ?? ''}>
								{categories.map((category, index) => {
									const record = scores.get([group?.id ?? '', category?.id ?? ''].join('-'));

									return (
										<Score
											key={category?.id ?? index}
											value={record?.score ?? undefined}
											delta={record?.delta ?? undefined}
										/>
									);
								})}
								<Box ml={4}>
									<Score
										value={rate?.participation ?? undefined}
										delta={rate?.delta ?? undefined}
										scale={100}
										suffix="%"
									/>
								</Box>
							</Row>
						);
					})}
				</Body>
			</Main>
		</Flex>
	);
}
